import React from 'react';
import { ArrowRight, Sparkles, Target, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const scrollToBooking = () => {
    document.getElementById('booking-form').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    document.getElementById('services').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen bg-gradient-to-br from-[#f7f5f2] via-white to-[#f7f5f2] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#5f9dff]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#61525a]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-gradient-to-r from-[#5f9dff]/5 to-[#61525a]/5 rounded-full blur-2xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center bg-white/80 backdrop-blur-sm border border-[#5f9dff]/20 rounded-full px-4 py-2 mb-8 shadow-sm">
              <Sparkles className="w-4 h-4 text-[#5f9dff] mr-2" />
              <span className="text-sm font-medium text-[#61525a]">IIT Alumni-Driven Initiative</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e1919] mb-6 leading-tight font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
              Your Sharp Edge in
              <span className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] bg-clip-text text-transparent"> Market & Business Intelligence</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-[#736c64] mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Empowering students, startups, and corporates with deep research insights, actionable intelligence, and strategic growth support through expert analysis and proven methodologies.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                onClick={scrollToBooking}
                className="bg-[#61525a] hover:bg-[#4a3f47] text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl group"
              >
                Book Service Inquiry
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
              
              <Button 
                onClick={scrollToServices}
                variant="outline"
                className="border-2 border-[#5f9dff] text-[#5f9dff] hover:bg-[#5f9dff] hover:text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Explore Services
              </Button>
            </div>
            
            {/* Enhanced Analytics Stats */}
            <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-gray-200/50">
              <div className="text-center group">
                <div className="relative">
                  <div className="text-3xl font-bold text-[#61525a] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif] group-hover:scale-110 transition-transform duration-300">100+</div>
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-[#5f9dff] rounded-full animate-pulse opacity-70"></div>
                </div>
                <div className="text-sm text-[#736c64] mt-2 font-medium">Projects Delivered</div>
                <div className="text-xs text-[#5f9dff] mt-1">↗ 25% this quarter</div>
              </div>
              <div className="text-center group">
                <div className="relative">
                  <div className="text-3xl font-bold text-[#61525a] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif] group-hover:scale-110 transition-transform duration-300">₹50L+</div>
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-[#ff8c19] rounded-full animate-pulse opacity-70"></div>
                </div>
                <div className="text-sm text-[#736c64] mt-2 font-medium">Value Generated</div>
                <div className="text-xs text-[#ff8c19] mt-1">📈 ROI for clients</div>
              </div>
              <div className="text-center group">
                <div className="relative">
                  <div className="text-3xl font-bold text-[#61525a] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif] group-hover:scale-110 transition-transform duration-300">98%</div>
                  <div className="absolute -top-2 -right-2 w-3 h-3 bg-[#b4dc19] rounded-full animate-pulse opacity-70"></div>
                </div>
                <div className="text-sm text-[#736c64] mt-2 font-medium">Client Satisfaction</div>
                <div className="text-xs text-[#b4dc19] mt-1">⭐ 4.9/5 average rating</div>
              </div>
            </div>
          </div>
          
          {/* Right Content - Visual */}
          <div className="relative">
            <div className="relative mx-auto max-w-md lg:max-w-lg">
              {/* Main Logo/Visual */}
              <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_7be45cf1-2732-4548-9b9b-de308b396d7f/artifacts/1xjolion_Logo%20Knowledge%20Ninja.PNG" 
                  alt="Knowledge Ninja" 
                  className="w-full h-auto max-w-xs mx-auto transform transition-transform duration-1000 hover:scale-110"
                />
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 bg-[#5f9dff] rounded-2xl p-4 shadow-xl animate-bounce delay-300">
                <Target className="w-8 h-8 text-white" />
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-[#61525a] rounded-2xl p-4 shadow-xl animate-bounce delay-700">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute top-1/2 -left-8 w-16 h-16 bg-gradient-to-r from-[#5f9dff]/20 to-transparent rounded-full blur-xl animate-pulse"></div>
              <div className="absolute top-1/4 -right-8 w-20 h-20 bg-gradient-to-l from-[#61525a]/20 to-transparent rounded-full blur-xl animate-pulse delay-500"></div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-[#61525a] rounded-full flex justify-center">
            <div className="w-1 h-3 bg-[#61525a] rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { HeroSection };